// src/lib/conversation-api.js — PARLER À L'OPÉRATEUR.
//
// ══ CE QUE CE MODULE FAIT ═══════════════════════════════════════════════════
//
// Il envoie la question de Dimitri avec son écran courant, et rend la réponse
// VALIDÉE PAR LE SERVEUR — répondre, montrer, ou emmener.
//
// ⚠️ IL NE RE-VALIDE RIEN, ET C'EST VOLONTAIRE. La validation vit côté serveur
// (lib/reponse-registres.js), un seul endroit. Deux validations qui divergent,
// c'est deux vérités sur ce que l'opérateur a le droit de faire — et on ne
// saurait pas laquelle croit l'écran.
//
// ⚠️ EN REVANCHE IL NE FAIT JAMAIS CONFIANCE AU RÉSEAU. Une réponse qui
// n'arrive pas, ou qui n'a pas la forme attendue, devient une phrase qui le
// DIT. Un champ qui avale ce qu'on lui écrit serait pire que pas de champ —
// c'est déjà la doctrine du bloc quand la conversation était fermée.

import { supabase } from "../supabase";
import {
  filPourServeur,
  idDuFil,
  formulaireEnCours,
  confirmationEnCours,
} from "./fil-operateur";

const MESSAGE_PANNE =
  "Je n'ai pas pu joindre l'opérateur. Rien n'a été perdu : votre texte est resté ici.";

/**
 * @param {string} question
 * @param {{ecran?:string}|null} contexte — ce que l'écran déclare
 * @returns {Promise<{registre:string, texte:string, vue?:object, destination?:object}>}
 *          Ne LANCE jamais : une panne est une réponse, pas une exception.
 */
export async function demander(question, contexte) {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) {
      return {
        registre: "repondre",
        texte: "Votre session a expiré — reconnectez-vous.",
      };
    }
    const res = await fetch("/api/beds24-mapping", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        action: "operateur_conversation",
        question,
        contexte,
        // ⚠️ CE QU'ON VIENT DE SE DIRE. Le serveur le borne et le traite comme
        // un SUJET, jamais comme une donnée — les chiffres sont relus à chaque
        // tour côté serveur.
        fil: filPourServeur(),
        // L'identifiant de la conversation : le rappel des souvenirs ne doit
        // pas ressortir ce qui est déjà dans ce fil.
        fil_id: idDuFil(),
        // ⚠️ LE FORMULAIRE EN COURS, ENVOYÉ À PART. `filPourServeur` ne le
        // transporte pas (allow-list) : c'est une DÉCLARATION, que le serveur
        // re-valide champ par champ et dont il jette ce qui n'entre pas au
        // schéma. Absent tant qu'aucun formulaire n'est ouvert.
        ...(formulaireEnCours() ? { formulaire: formulaireEnCours() } : {}),
        // ⚠️ LE NOM QUI ATTEND UN « OUI ». Même nature que le formulaire : une
        // DÉCLARATION, re-validée et re-résolue côté serveur — elle ne porte
        // qu'un NOM, jamais un identifiant. Sans ce renvoi, la question « c'est
        // bien celui-là ? » n'aurait aucune réponse possible et « oui »
        // rebouclerait indéfiniment.
        ...(confirmationEnCours()
          ? { confirmation: confirmationEnCours() }
          : {}),
      }),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json || json.ok !== true || !json.reponse) {
      return { registre: "repondre", texte: MESSAGE_PANNE };
    }
    const r = json.reponse;
    // Garde de forme minimale : on ne DOUBLE pas la validation serveur, mais on
    // refuse une réponse qui n'a pas de registre — l'écran ne saurait pas quoi
    // en faire, et afficherait du vide.
    if (!r || typeof r.registre !== "string") {
      return { registre: "repondre", texte: MESSAGE_PANNE };
    }
    return r;
  } catch (e) {
    console.error("[conversation]", e?.message || e);
    return { registre: "repondre", texte: MESSAGE_PANNE };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// demanderEnFlux — LES PREMIERS MOTS EN UNE SECONDE, AU LIEU DU PARAGRAPHE EN
// QUINZE.
// ─────────────────────────────────────────────────────────────────────────────
//
// ⚠️ POURQUOI. Mesuré le 2026-09-09 : médiane 15 s, jusqu'à 28 s. Dimitri :
// « quinze secondes, je l'abandonne ». Les faits coûtent ~1,9 s ; tout le reste
// est le modèle qui écrit. Le total ne baisse pas — l'attente, si.
//
// ⚠️ CE QUI S'AFFICHE AU VOL N'EST PAS CE QUI FAIT FOI. La prose arrive par
// morceaux ; la RÉPONSE VALIDÉE arrive à la fin, et elle REMPLACE ce qui était
// affiché. Si le modèle a reformulé en cours de route, c'est sa version finale
// qui gagne. Et une vue ou une destination n'existent QUE dans cette réponse
// finale : l'écran ne bouge jamais sur une phrase à moitié arrivée.
//
// ⚠️ IL NE LANCE JAMAIS, comme `demander`. Une panne est une réponse.
//
// @param {(prose:string) => void} onMot — appelé avec la prose CUMULÉE.
// @returns {Promise<object>} la réponse validée par le serveur.
export async function demanderEnFlux(question, contexte, onMot, onEtape) {
  const dire = (p) => {
    try {
      if (typeof onMot === "function") onMot(p);
    } catch (e) {
      // Un afficheur qui plante ne doit pas emporter la réponse.
      console.error("[conversation] afficheur", e?.message || e);
    }
  };
  const etape = (m) => {
    try {
      if (typeof onEtape === "function") onEtape(m);
    } catch (e) {
      console.error("[conversation] étape", e?.message || e);
    }
  };
  // ══ LA DATE LIMITE DU NAVIGATEUR ══════════════════════════════════════════
  //
  // ⚠️ MESURÉ EN PROD LE 2026-09-13, en le challengeant : sur six demandes de
  // « prépare une réponse », CINQ n'ont jamais rendu la main — 240 secondes
  // d'attente, et l'écran serait resté sur « il réfléchit… » indéfiniment.
  //
  // La cause n'est pas le modèle : la fonction serveur est tuée à 60 s
  // (`maxDuration` de vercel.json, et le même plafond côté appel modèle). Mais
  // quand elle meurt EN PLEIN FLUX, la connexion ne se referme pas côté
  // navigateur : le lecteur attend un `done` qui n'arrivera jamais. Le repli
  // « le flux s'est arrêté sans conclure », juste en dessous, ne pouvait donc
  // pas s'exécuter — il attendait la fin d'un flux qui ne finissait pas.
  //
  // D'où une limite ICI, un peu au-dessus de celle du serveur : ce qui a été
  // écrit est rendu, et si rien n'est venu, on le DIT. Une attente sans fin est
  // la pire des réponses — elle n'apprend rien et elle ne se termine jamais.
  const LIMITE_MS = 75000;
  // Déclarée AVANT le `try` : le repli d'abandon doit pouvoir rendre ce qui a
  // déjà été écrit, et une variable née dans le `try` n'existe pas au `catch`.
  let prose = "";
  const horloge = new AbortController();
  const minuteur = setTimeout(() => horloge.abort(), LIMITE_MS);
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) {
      return {
        registre: "repondre",
        texte: "Votre session a expiré — reconnectez-vous.",
      };
    }
    const res = await fetch("/api/beds24-mapping", {
      method: "POST",
      signal: horloge.signal,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        action: "operateur_conversation",
        question,
        contexte,
        fil: filPourServeur(),
        fil_id: idDuFil(),
        // Le formulaire en cours — même déclaration qu'en mode complet : les
        // deux chemins doivent voir exactement la même chose.
        ...(formulaireEnCours() ? { formulaire: formulaireEnCours() } : {}),
        // ⚠️ LE NOM QUI ATTEND UN « OUI ». Même nature que le formulaire : une
        // DÉCLARATION, re-validée et re-résolue côté serveur — elle ne porte
        // qu'un NOM, jamais un identifiant. Sans ce renvoi, la question « c'est
        // bien celui-là ? » n'aurait aucune réponse possible et « oui »
        // rebouclerait indéfiniment.
        ...(confirmationEnCours()
          ? { confirmation: confirmationEnCours() }
          : {}),
        flux: true,
      }),
    });
    // ⚠️ REPLI SUR LE MODE COMPLET. Un navigateur sans `body.getReader`, un
    // proxy qui refuse le flux, une erreur HTTP : on repasse par `demander`
    // plutôt que d'échouer. Le flux est un confort, pas une dépendance.
    if (!res.ok || !res.body || typeof res.body.getReader !== "function") {
      return await demander(question, contexte);
    }

    const lecteur = res.body.getReader();
    const decodeur = new TextDecoder();
    let reste = "";
    let finale = null;
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await lecteur.read();
      if (done) break;
      // Un paquet réseau ne respecte pas les frontières de lignes : sans ce
      // reliquat, un objet coupé en deux disparaît au milieu d'une phrase.
      reste += decodeur.decode(value, { stream: true });
      const lignes = reste.split("\n");
      reste = lignes.pop() || "";
      for (const ligne of lignes) {
        if (!ligne.trim()) continue;
        let ev;
        try {
          ev = JSON.parse(ligne);
        } catch {
          continue; // une ligne illisible n'arrête pas la lecture
        }
        if (ev.t === "debut") {
          // ⚠️ LE SERVEUR A PRIS LA QUESTION. On ne montre pas encore de texte
          // — il n'y en a pas — mais on DIT où il en est. Une attente muette
          // est indiscernable d'une panne, et c'est ce que Dimitri a signalé
          // le 2026-09-09 : « entre le moment où j'envoie et le moment où
          // quelque chose se passe, c'est lent ».
          etape("je regarde…");
        } else if (ev.t === "etape" && typeof ev.v === "string") {
          // ⚠️ CE QU'IL DIT EST VRAI. Chaque jalon vient d'une source qui vient
          // RÉELLEMENT de répondre — ce n'est pas un scénario joué pendant
          // qu'on attend. Une barre de progression qui avance toute seule est
          // un mensonge poli.
          etape(ev.v);
        } else if (ev.t === "mot" && typeof ev.v === "string") {
          prose += ev.v;
          dire(prose);
        } else if (ev.t === "fin" || ev.t === "panne") {
          finale = ev.reponse;
        }
      }
    }
    if (!finale || typeof finale.registre !== "string") {
      // Le flux s'est arrêté sans conclure. Ce qui est arrivé est arrivé : on
      // rend la prose plutôt que de la jeter — mais en `repondre`, jamais avec
      // une destination qu'on n'a pas lue.
      return prose
        ? { registre: "repondre", texte: prose, interrompu: true }
        : { registre: "repondre", texte: MESSAGE_PANNE };
    }
    return finale;
  } catch (e) {
    // ⚠️ LA COUPURE SE DIT, ET CE QUI EST ÉCRIT EST RENDU. Un abandon après
    // 75 s n'est pas une panne quelconque : c'est une question trop lourde, et
    // Dimitri doit le savoir pour la reposer autrement — pas rester devant un
    // écran qui tourne.
    if (e && e.name === "AbortError") {
      console.error("[conversation/flux] abandon après", LIMITE_MS, "ms");
      return prose
        ? { registre: "repondre", texte: prose, interrompu: true }
        : {
            registre: "repondre",
            texte:
              "Il n’a pas répondu en 75 secondes — la question était trop lourde pour un seul tour. Reposez-la en plus court, ou demandez-lui une chose à la fois.",
            interrompu: true,
          };
    }
    console.error("[conversation/flux]", e?.message || e);
    return { registre: "repondre", texte: MESSAGE_PANNE };
  } finally {
    clearTimeout(minuteur);
  }
}

export { MESSAGE_PANNE };

// ─────────────────────────────────────────────────────────────────────────────
// demanderBrief — CE QU'IL DIT SANS QU'ON LUI DEMANDE
// ─────────────────────────────────────────────────────────────────────────────
//
// Dimitri, 2026-09-09 : « il ne me dit rien de lui-même ».
//
// ⚠️ LE SILENCE EST LA RÉPONSE NORMALE. Le serveur ne parle que si quelque
// chose a changé depuis la dernière fois (`lib/operateur-brief.js`) ; une
// panne, une session expirée ou une réponse malformée se traitent EXACTEMENT
// comme « rien à dire ». Un message d'excuse chaque matin serait précisément
// le bandeau qu'on refuse — et il n'y a rien à sauver ici : Dimitri n'a rien
// écrit, rien ne peut être perdu.
// ⚠️ PLUS AUCUN APPELANT DEPUIS LE 2026-09-15 (#1433) : le bandeau qui portait
// le brief a été retiré de l'accueil, et cet écran ne le demande plus — garder
// une requête dont personne ne lit la réponse, c'est payer un appel au modèle
// pour rien. L'endpoint `operateur_brief` et `lib/operateur-brief.js` restent
// intacts ; cette fonction est le fil qui les rebranchera le jour où Dimitri
// voudra de nouveau un mot d'ouverture.
export async function demanderBrief() {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) return null;
    const res = await fetch("/api/beds24-mapping", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ action: "operateur_brief" }),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json || json.ok !== true || json.parle !== true)
      return null;
    return typeof json.texte === "string" && json.texte.trim()
      ? json.texte.trim()
      : null;
  } catch (e) {
    console.error("[brief]", e?.message || e);
    return null;
  }
}
